import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAutomationsApiFacade } from '../../../../apis/facades/useAutomationsApiFacade';
import { useCrmOrgsApiFacade } from '../../../../apis/facades/useCrmOrgsApiFacade';
import { useRollupsApiFacade } from '../../../../apis/facades/useRollupsApiFacade';
import {
  selectLayoutsByObjectName,
  selectDependenciesHistory,
  selectDependenciesConfigurationItem,
  setDependenciesConfigurationItem,
  setOpenAiDescriptionForRule,
  setConfigurationDependenciesForRule,
  setConfigurationUsedByForRule,
  setConfigurationUsedByForField,
  setLatestDeployment,
  clearDependencies,
  goBackInHistory,
  setConfigurationUserInputs,
  setSelectedDependencyContentType,
  setSourceCodeForConfiguration,
} from '../../../../reducers/documentationReducer';
import { telemetry } from '../../../../telemetry';
import extractErrorMsg from '../../../helpers/extractErrorMsg';
import { SweepElementTypes } from '../../types';
import { isFieldType, formatOpenAiResponse } from '../helpers';
import { NewDependencyProps, ConfigurationType, DependenciesContentViewType } from '../types';
import { useGetConfigurationItem } from './useGetConfigurationItem';
import { UserInputsByConfigurationId } from '@server/user-inputs';
import { useUserInputsApiFacade } from '../../../../apis/facades/useUserInputsApiFacade';
import { useLayouts } from '../../hooks/useLayouts';
import useParser from '../../../parser/useParser';
import { DocumentationDialogContext } from '../../context/DocumentationContextProvider';

export const useDependencies = (crmOrgId: string) => {
  const dispatch = useDispatch();

  const {
    post_crmOrgGenerateConfigurationGpt,
    get_crmOrgConfigurationConsumersFromSalto,
    get_crmOrgConfigurationDependenciesFromSalto,
    get_crmOrgFieldConsumerFromSalto,
    get_NaclFile,
  } = useCrmOrgsApiFacade();

  const { put_userInputsForConfiguration } = useUserInputsApiFacade();

  const { get_automation } = useAutomationsApiFacade();
  const { get_rollup } = useRollupsApiFacade();
  const { fetchLayoutsData } = useLayouts();
  const { parseObjectOnDemand } = useParser({ crmOrgId });

  const { recordTypesUsedInCanvas: recordTypeNamesUsedInCanvas } = useContext(
    DocumentationDialogContext,
  );

  const layoutsByObjectName = useSelector(selectLayoutsByObjectName(crmOrgId));
  const historyLength = useSelector(selectDependenciesHistory);
  const dependenciesData = useSelector(selectDependenciesConfigurationItem);
  const selectedDependency = useSelector(selectDependenciesConfigurationItem);
  const firstHistoryItem = selectedDependency?.history?.[0];

  const configurationItem = useGetConfigurationItem({ crmOrgId, recordTypeNamesUsedInCanvas });

  const handleSetConfigurationItem = useCallback(
    (props: NewDependencyProps) => {
      const { dependencyType, parentType, id, name, objectName, clearHistory, contentType } = props;
      if (isFieldType(parentType)) {
        dispatch(setDependenciesConfigurationItem(props));
        return;
      }

      if (parentType === ConfigurationType.layouts) {
        const layouts = layoutsByObjectName?.[objectName ?? ''];

        if (!layouts?.layouts && objectName) {
          fetchLayoutsData({ crmOrgId, objectName: objectName });
        }
      }

      dispatch(
        setDependenciesConfigurationItem({
          id,
          dependencyType,
          parentType: parentType as ConfigurationType,
          name,
          clearHistory,
          objectName,
          contentType,
        }),
      );
    },
    [dispatch, crmOrgId, fetchLayoutsData, layoutsByObjectName],
  );

  const getOpenAiDescriptionForRule = useCallback(
    async ({
      objectName,
      ruleType,
      ruleName,
      ruleId,
      crmOrgId,
    }: {
      objectName: string;
      ruleType: string;
      ruleName: string;
      ruleId: string;
      crmOrgId: string;
    }) => {
      try {
        const res = await post_crmOrgGenerateConfigurationGpt({
          crmOrgId,
          payload: {
            objectName,
            configurationKey: ruleType,
            configurationName: ruleName,
          },
        });
        const openAiDescription = formatOpenAiResponse(res);

        dispatch(
          setOpenAiDescriptionForRule({
            crmOrgId,
            openAiDescription,
            ruleId,
          }),
        );
      } catch (e) {
        telemetry.captureError(extractErrorMsg(e));
        dispatch(
          setOpenAiDescriptionForRule({
            crmOrgId,
            openAiDescription: {
              type: 'error',
              text: 'Something went wrong, please try again later',
            },
            ruleId,
          }),
        );
      }
    },
    [dispatch, post_crmOrgGenerateConfigurationGpt],
  );

  const getConfigurationDependencies = useCallback(
    async ({
      ruleType,
      ruleId,
      crmOrgId,
      objectName,
      configurationName,
    }: {
      ruleType: string;
      ruleId: string;
      crmOrgId: string;
      objectName: string;
      configurationName: string;
    }) => {
      try {
        const dependsOn = await get_crmOrgConfigurationDependenciesFromSalto({
          crmOrgId,
          configurationKey: ruleType,
          objectName,
          configurationName,
        });

        dispatch(
          setConfigurationDependenciesForRule({
            crmOrgId,
            ruleId,
            dependsOn,
          }),
        );
      } catch (e) {
        telemetry.captureError(extractErrorMsg(e));
      }
    },
    [dispatch, get_crmOrgConfigurationDependenciesFromSalto],
  );

  const getConfigurationUsedBy = useCallback(
    async ({
      ruleType,
      ruleId,
      crmOrgId,
      configurationName,
      objectName,
    }: {
      ruleType: string;
      ruleId: string;
      crmOrgId: string;
      configurationName: string;
      objectName: string;
    }) => {
      try {
        const usedBy = await get_crmOrgConfigurationConsumersFromSalto({
          crmOrgId,
          configurationKey: ruleType,
          configurationName,
          objectName,
        });

        dispatch(
          setConfigurationUsedByForRule({
            crmOrgId,
            ruleId,
            usedBy,
          }),
        );
      } catch (e) {
        telemetry.captureError(extractErrorMsg(e));
      }
    },
    [dispatch, get_crmOrgConfigurationConsumersFromSalto],
  );

  const getFieldConsumers = useCallback(
    async ({
      objectName,
      fieldName,
      id,
      crmOrgId,
    }: {
      objectName: string;
      fieldName: string;
      id: string;
      crmOrgId: string;
    }) => {
      try {
        const usedBy = await get_crmOrgFieldConsumerFromSalto({
          crmOrgId,
          objectName,
          fieldName,
        });

        dispatch(
          setConfigurationUsedByForField({
            crmOrgId,
            usedBy,
            fieldName,
            objectName,
            id,
          }),
        );
      } catch (e) {
        telemetry.captureError(extractErrorMsg(e));
      }
    },
    [dispatch, get_crmOrgFieldConsumerFromSalto],
  );

  const fetchLatestDeployedVersion = useCallback(
    async (sweepElementType: SweepElementTypes, elementId: string, crmOrgId: string) => {
      let element: any;

      switch (sweepElementType) {
        case ConfigurationType.sweepAutomation:
        case ConfigurationType.sweepAssignment:
        case ConfigurationType.sweepScheduledAssignment:
        case ConfigurationType.sweepAlert:
        case ConfigurationType.sweepMatching:
        case ConfigurationType.sweepDedupe:
          element = await get_automation(elementId, crmOrgId, true);
          break;
        case ConfigurationType.rollups:
          element = await get_rollup(elementId, crmOrgId, true);
          break;
        default:
          break;
      }

      if (element.readVersion) {
        dispatch(setLatestDeployment({ elementId, readOnlyElement: element.readVersion }));
        return;
      } else {
        telemetry.captureError(
          new Error('Read version was not returned for crmOrgId and element '),
          {
            crmOrgId,
            elementId,
          },
        );
        return;
      }
    },
    [dispatch, get_automation, get_rollup],
  );

  const updateDependencyTags = useCallback(
    (userInputs: UserInputsByConfigurationId, notParsedObjects?: string[]) => {
      try {
        dispatch(setConfigurationUserInputs({ userInputs }));
        put_userInputsForConfiguration({ crmOrgId, userInputs });

        if (notParsedObjects && notParsedObjects.length > 0) {
          notParsedObjects.forEach((objectName) => parseObjectOnDemand(objectName));
        }
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [crmOrgId, put_userInputsForConfiguration, dispatch, parseObjectOnDemand],
  );

  const handleCloseDependencies = useCallback(() => {
    dispatch(clearDependencies());
  }, [dispatch]);

  const onGoBackInHistory = useCallback(() => {
    dispatch(goBackInHistory());
  }, [dispatch]);

  const setContentType = useCallback(
    (contentType: DependenciesContentViewType) => {
      dispatch(setSelectedDependencyContentType({ contentType }));
    },
    [dispatch],
  );

  const onGoToDependenciesMainView = useCallback(() => {
    setContentType(DependenciesContentViewType.default);
  }, [setContentType]);

  const getSourceCode = useCallback(
    async (filename: string, configurationId: string) => {
      try {
        const sourceCode = await get_NaclFile({ crmOrgId, filename });
        dispatch(setSourceCodeForConfiguration({ crmOrgId, sourceCode, configurationId }));
      } catch (error) {
        telemetry.captureError(error);
      }
    },
    [crmOrgId, get_NaclFile, dispatch],
  );

  return {
    handleSetConfigurationItem,
    getOpenAiDescriptionForRule,
    updateDependencyTags,

    getConfigurationUsedBy,
    getConfigurationDependencies,
    getFieldConsumers,
    fetchLatestDeployedVersion,

    handleCloseDependencies,
    onGoBackInHistory,
    onGoToDependenciesMainView,
    setContentType,
    getSourceCode,

    historyLength,
    dependenciesData,
    configurationItem,
    activeItemId: firstHistoryItem?.id
      ? firstHistoryItem.id
      : firstHistoryItem?.name + (firstHistoryItem?.objectName ?? ''),
  };
};
